import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Card, CardBody, CardFooter, CardHeader } from 'reactstrap';
import { constants } from '../../../config';
import { useSelector } from 'react-redux';
import getChartColorsArray from '../../Common/ChartsDynamicColor';

mapboxgl.accessToken = constants.REACT_APP_MAPBOX;

const MapMultiple = ({
	title,
	points,
	zoom,
	handleMarkerClick,
	selectedEstablishmentId,
	customMarker,
}) => {
	const mapContainer = useRef(null);
	const mapInstance = useRef(null);
	const markersRef = useRef([]);
	const zoomDebounceTimeoutRef = useRef(null);

	// Selector directo para el modo de layout
	const layoutModeType = useSelector(state => state.Layout.layoutModeType);

	// Carga de colores desde la configuración
	const dataColors = '["--vz-primary", "--vz-secondary"]';
	const revenueExpensesChartsColors = getChartColorsArray(dataColors);

	// Estilo dinámico del mapa
	const mapStyle =
		layoutModeType === 'dark'
			? 'mapbox://styles/mapbox/dark-v10'
			: 'mapbox://styles/mapbox/light-v11';

	useEffect(() => {
		const validPoints = points.filter(
			point =>
				Number.isFinite(Number(point.longitude)) &&
				Number.isFinite(Number(point.latitude))
		);

		// Inicializa el mapa
		const initialCenter =
			validPoints.length > 0
				? [validPoints[0].longitude, validPoints[0].latitude]
				: [0, 0];
		mapInstance.current = new mapboxgl.Map({
			container: mapContainer.current,
			style: mapStyle,
			center: initialCenter,
			zoom: zoom,
		});

		// Controles del mapa
		mapInstance.current.addControl(
			new mapboxgl.NavigationControl(),
			'top-left'
		);
		mapInstance.current.addControl(
			new mapboxgl.ScaleControl({ maxWidth: 80, unit: 'metric' }),
			'bottom-right'
		);
		mapInstance.current.addControl(
			new mapboxgl.GeolocateControl({
				positionOptions: { enableHighAccuracy: true },
				trackUserLocation: true,
			}),
			'top-left'
		);

		const map = mapInstance.current;
		let selectedPoint = null;

		// Función para ajustar la visibilidad de los nombres
		const adjustNameVisibility = () => {
			if (!selectedPoint && selectedEstablishmentId) {
				selectedPoint = validPoints.find(
					point => point.id === selectedEstablishmentId
				);
			}

			const currentZoom = map.getZoom();
			markersRef.current.forEach(({ nameElement, marker }) => {
				if (nameElement) {
					const markerLngLat = marker.getLngLat();
					const isSelected = selectedPoint
						? markerLngLat.lng === selectedPoint.longitude &&
						  markerLngLat.lat === selectedPoint.latitude
						: false;

					if (isSelected) {
						nameElement.style.display = 'block';
					} else {
						nameElement.style.display =
							currentZoom >= 10 ? 'block' : 'none';
					}
				}
			});
		};

		const debouncedAdjustNameVisibility = () => {
			if (zoomDebounceTimeoutRef.current) {
				clearTimeout(zoomDebounceTimeoutRef.current);
			}

			zoomDebounceTimeoutRef.current = setTimeout(() => {
				adjustNameVisibility();
			}, 80);
		};

		// Agrega los marcadores al mapa
		addMarkersToMap(map, validPoints);

		// Escucha el evento de zoom
		map.on('zoom', debouncedAdjustNameVisibility);

		// Ajusta inicialmente la visibilidad según el nivel de zoom
		adjustNameVisibility();

		return () => {
			try {
				// Limpia los marcadores
				markersRef.current.forEach(({ marker }) => {
					if (marker instanceof mapboxgl.Marker) {
						marker.remove();
					}
				});
				markersRef.current = [];

				if (zoomDebounceTimeoutRef.current) {
					clearTimeout(zoomDebounceTimeoutRef.current);
					zoomDebounceTimeoutRef.current = null;
				}

				// Limpia el mapa y elimina el listener de zoom
				if (mapInstance.current) {
					mapInstance.current.off('zoom', debouncedAdjustNameVisibility);
					mapInstance.current.remove();
					mapInstance.current = null;
				}
			} catch (error) {
				console.error('Error al desmontar el mapa:', error);
			}
		};
	}, [points, mapStyle, zoom, handleMarkerClick, selectedEstablishmentId]);

	const addMarkersToMap = (map, points) => {
		// Limpia los marcadores existentes
		markersRef.current.forEach(({ marker }) => {
			if (marker instanceof mapboxgl.Marker) {
				marker.remove();
			}
		});
		markersRef.current = [];

		points.forEach(point => {
			const markerContainer = document.createElement('div');
			const markerElement = document.createElement('div');
			const nameElement = document.createElement('span');

			// Configura estilos y clases
			const isSelected = point.id === selectedEstablishmentId;
			markerElement.className = `marker ${
				isSelected ? 'selected' : 'default'
			}`;

			// Use custom marker function if provided, otherwise use default logic
			if (customMarker) {
				customMarker(point, markerElement, isSelected);
			} else {
				// Default logic
				if (point.sensor_failure_status?.bgcolor) {
					markerElement.style.backgroundColor =
						point.sensor_failure_status?.txcolor;
					markerElement.title = point.sensor_failure_status?.name;
				}
			}

			nameElement.textContent = point.name;
			nameElement.className = 'marker-name';
			nameElement.style.display = 'none'; // Inicialmente oculto

			// Construye el marcador y agrega elementos
			markerContainer.appendChild(markerElement);
			markerContainer.appendChild(nameElement);

			const marker = new mapboxgl.Marker({ element: markerContainer })
				.setLngLat([point.longitude, point.latitude])
				.addTo(map)
				.setPopup(
					new mapboxgl.Popup({ offset: 25 }).setHTML(point.info || point.name || '')
				);

			// Manejo de clic en el marcador
			marker.getElement().addEventListener('click', () => {
				handleMarkerClick(point);
				map.flyTo({ center: [point.longitude, point.latitude], zoom: 10 });
			});

			markersRef.current.push({ marker, nameElement });
		});
	};

	// Mueve el mapa al marcador seleccionado con zoom fijo en 10
	useEffect(() => {
		if (mapInstance.current && selectedEstablishmentId) {
			const selectedPoint = points.find(
				point => point.id === selectedEstablishmentId
			);
			if (selectedPoint) {
				mapInstance.current.flyTo({
					center: [selectedPoint.longitude, selectedPoint.latitude],
					zoom: 10,
				});
			}
		}
	}, [selectedEstablishmentId, points]);

	return (
		<Card className='p-0 mb-10 card-height-100'>
			{title && (
				<CardHeader className='p-2'>
					<h5 className='card-title mb-0'>
						<i className='ri-pushpin-line align-middle me-1 text-muted'></i>
						{title}
					</h5>
				</CardHeader>
			)}
			<CardBody className='m-0 p-2'>
				<div
					ref={mapContainer}
					style={{ width: '100%', minHeight: '100%' }}
				></div>
			</CardBody>

			{selectedEstablishmentId == null && (
				<CardFooter className='m-0 p-2'>
					<div className='alert alert-info m-0' role='alert'>
						Puede seleccionar un punto del mapa para obtener su
						información
					</div>
				</CardFooter>
			)}
		</Card>
	);
};

export default MapMultiple;